import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { useFullscreen } from '@neptune/shared/common-business-logic';
import { useKeyboardShortcut } from '@neptune/shared/keyboard-business-logic';
import { TOGGLE_DISTRACTION_FREE_MODE_SHORTCUT } from '@neptune/structure-domain';

import { navigateTo } from './actions';
import {
  createCurrentRouteParamSelector,
  getCurrentRouteName,
  getCurrentRouteParams,
} from './selectors';

const distractionFreeSelector = createCurrentRouteParamSelector('distraction-free');
export const getDistractionFree = createSelector(distractionFreeSelector, (value) => !!value);

export const getCurrentRouteParamsWithoutDistractionFree = createSelector(
  getCurrentRouteParams,
  ({ 'distraction-free': valueToDiscard, ...rest }) => rest,
);

export const useDistractionFree = () => {
  const isDistractionFree = useSelector(getDistractionFree);
  const dispatch = useDispatch();
  const routeName = useSelector(getCurrentRouteName);
  const routeParams = useSelector(getCurrentRouteParams);
  const routeParamsWithoutDistractionFree = useSelector(
    getCurrentRouteParamsWithoutDistractionFree,
  );
  const { fullscreen, maximize, restore } = useFullscreen();

  React.useEffect(() => {
    if (isDistractionFree && !fullscreen) {
      maximize();
    }

    if (!isDistractionFree && fullscreen) {
      restore();
    }
  }, [fullscreen, isDistractionFree, maximize, restore]);

  const routeParamsWithDistractionFree = { ...routeParams, 'distraction-free': '1' };

  useKeyboardShortcut(
    TOGGLE_DISTRACTION_FREE_MODE_SHORTCUT,
    () => {
      if (isDistractionFree) {
        dispatch(navigateTo(routeName, routeParamsWithoutDistractionFree));
      } else {
        dispatch(navigateTo(routeName, routeParamsWithDistractionFree));
      }
    },
    { disableOnModalOpen: false },
  );

  return {
    isDistractionFree,
    routeParams: isDistractionFree
      ? routeParamsWithoutDistractionFree
      : routeParamsWithDistractionFree,
    routeName,
  };
};
